export default {
  format_stage_label(stage) {
    const stage_labels = {
      QUERY_PROCESSOR: "Preparing request",
      FAST_GENERAL_RESPONSE: "Answering directly",
      CIRCUIT_BREAKER: "Safety check",
      GATEKEEPER: "Checking scope",
      INTENT: "Understanding intent",
      ARCHITECT: "Planning data retrieval",
      PROPERTY_ACCESS: "Checking property access",
      RETRIEVAL: "Fetching dashboard data",
      GENERATION: "Writing answer",
      VALIDATION: "Validating evidence",
      FINAL: "Finalizing",
    };

    if (stage_labels[String(stage || "")]) {
      return stage_labels[String(stage || "")];
    }

    return String(stage || "processing")
      .toLowerCase()
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  },

  format_query_status(status_value) {
    return this.format_stage_label(status_value || "processing");
  },

  format_stage_detail(log, query) {
    const payload = this.get_log_payload(log);

    if (this.is_failed_stage(log, query)) return this.get_payload_error_message(payload) || "This stage could not complete.";
    if (this.is_final_stage(log, query)) return "Final answer is ready.";

    const static_details = {
      QUERY_PROCESSOR: "Preparing the query and chat context.",
      FAST_GENERAL_RESPONSE: "No dashboard retrieval is needed for this answer.",
      CIRCUIT_BREAKER: "Checking whether the request can be handled safely.",
      GATEKEEPER: "Confirming the request belongs to Jeffrey.",
      GENERATION: "Turning retrieved data into a clear answer.",
      VALIDATION: "Checking the answer against retrieved evidence.",
      FINAL: "Preparing the final response.",
    };

    if (static_details[log.stage]) return static_details[log.stage];
    if (log.stage === "INTENT") return this.get_intent_stage_detail(payload);
    if (log.stage === "ARCHITECT") return this.get_architect_stage_detail(payload);
    if (log.stage === "PROPERTY_ACCESS") return this.get_property_access_stage_detail(payload);
    if (log.stage === "RETRIEVAL") return this.get_retrieval_stage_detail(payload);

    return log.status === "SUCCESS" ? "Stage completed." : "Stage is running.";
  },

  is_failed_stage(log, query) {
    return log.status === "FAILED" || query.status === "FAILED";
  },

  is_final_stage(log, query) {
    return query.status === "COMPLETED" && log.stage === "FINAL";
  },

  get_log_payload(log) {
    return log?.payload && typeof log.payload === "object" ? log.payload : {};
  },

  get_payload_error_message(payload) {
    return payload.error_message || payload.error || payload.detail || "";
  },

  get_intent_stage_detail(payload) {
    const intent = payload.intent_code || payload.intent || payload.intent_name;
    const confidence = Number(payload.confidence_score ?? payload.confidence);

    if (intent && Number.isFinite(confidence)) {
      const confidence_percent = confidence > 1 ? confidence : confidence * 100;
      return `${this.format_stage_label(intent)} matched with ${Math.round(confidence_percent)}% confidence.`;
    }

    return intent
      ? `${this.format_stage_label(intent)} matched for this request.`
      : "Detecting request type, entities, and scope.";
  },

  get_architect_stage_detail(payload) {
    const selected_tool = payload.selected_tool || payload.tool || payload.capability_tool;
    const decision = payload.architect_decision || payload.decision;

    if (decision && String(decision).includes("STOP")) {
      return "Preparing a direct answer without dashboard retrieval.";
    }

    return selected_tool
      ? `Selected ${this.format_tool_name(selected_tool)} for verified data retrieval.`
      : "Choosing the safest data path for the request.";
  },

  get_property_access_stage_detail(payload) {
    const compare_ids = Array.isArray(payload.compare_property_ids)
      ? payload.compare_property_ids
      : [];
    const property_name = payload.property_name
      || payload.current_property_name
      || payload.architect_property_name;

    if (compare_ids.length) return `Checking access for ${compare_ids.length} selected properties.`;
    return property_name
      ? `Checking access to ${property_name}.`
      : "Checking property permissions for this request.";
  },

  get_retrieval_stage_detail(payload) {
    const row_count = payload.row_count ?? payload.rows_count ?? payload.count;
    const selected_tool = payload.selected_tool || payload.tool;
    const comparison_count = payload.comparison_property_count || payload.property_count;

    if (comparison_count) return `Retrieving comparison data for ${comparison_count} properties.`;
    if (row_count != null) return `Retrieved ${row_count} data ${Number(row_count) === 1 ? "row" : "rows"}.`;
    return selected_tool
      ? `Running ${this.format_tool_name(selected_tool)} against verified sources.`
      : "Retrieving verified dashboard data.";
  },

  format_tool_name(tool_name) {
    return String(tool_name || "")
      .replace(/^get_/, "")
      .replace(/_/g, " ")
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  },
};
